<template>
  <session-content-container :title="title">
    <template v-if="game && game.criticalResource" #left>
      <v-btn
        class="mx-1"
        color="accent"
        small
        dark
        @click="feverChartOpened = true"
      >
        <span v-text="$t('animation.multiproject.general.feverchart')" />
        <v-icon right>
          mdi-scatter-plot
        </v-icon>
      </v-btn>
    </template>
    <template #right>
      <v-btn
        class="mx-1"
        color="accent"
        small
        dark
        @click="drawListOpened = true"
      >
        <span v-text="$t('animation.multiproject.general.draw')" />
        <v-icon right>
          mdi-dice-multiple
        </v-icon>
      </v-btn>
    </template>

    <template v-if="game && game.criticalResource">
      <v-row no-gutters class="pb-3">
        <v-col
          v-for="(resource, index) in game.resourceList"
          :key="`resource-${index}`"
          class="px-1 pb-1"
          cols="12"
          sm="6"
          md="3"
        >
          <kpi-card :value="resource" />
        </v-col>
      </v-row>

      <player-card
        v-for="(player, playerIndex) in sortedPlayerList"
        :key="`player-${playerIndex}`"
        :value="player"
        ccpm
      />

      <fever-chart-container
        :game="game"
        :visible.sync="feverChartOpened"
      />
    </template>

    <!-- Critical resource not yet selected -->
    <template v-else-if="game">
      <critical-resource-select
        :game="game"
        :loading="loadingCritical"
        @input="setCriticalResource"
      />
    </template>

    <draw-container
      v-if="game"
      :visible.sync="drawListOpened"
      :game-id="gameId"
      :current-round="game.currentRound"
    />
  </session-content-container>
</template>

<script>
import _throttle from 'lodash/throttle'
import { mapActions } from 'vuex'
import Repositories from 'src/repositories'
import SessionContentContainer from '../SessionContentContainer'
import PlayerCard from '../../components/multi-project/PlayerCard'
import DrawContainer from './DrawContainer'
import FeverChartContainer from './FeverChartContainer'
import KpiCard from '../../components/multi-project/KpiCard'
import CriticalResourceSelect from '../../components/multi-project/CriticalResourceSelect'

export default {
  name: 'MultiProjectCcpmContainer',
  components: {
    SessionContentContainer,
    PlayerCard,
    DrawContainer,
    FeverChartContainer,
    KpiCard,
    CriticalResourceSelect,
  },
  props: {
    sessionId: {
      type: String,
      required: true,
    },
    gameId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    game: null,
    loading: true,
    drawListOpened: false,
    feverChartOpened: false,
    loadingCritical: false,
  }),
  computed: {
    gameTitle () {
      return this.$t('animation.menu.games.multiProjectCcpm')
    },
    title () {
      if (this.loading) {
        return this.$t('general.loading')
      }

      if (!this.game.criticalResource) {
        return this.gameTitle
      }

      const round = this.$tc('animation.multiproject.general.round')
      return `${this.gameTitle} - ${round} ${this.game.currentRound}`
    },
    sortedPlayerList () {
      return this.game.playerList
        .slice()
        .sort((a, b) => a.projectOrder - b.projectOrder)
    },
  },
  sockets: {
    trainingSessionPlayerLoggedIn ({ idSession }) {
      if (this.sessionId === idSession) {
        this.fetchData()
      }
    },
    async multiProjectSessionBulkChanges ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerWaitForStart ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCantStartProject ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerMustStartProject ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerResourcesSelection ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerWaitForResourcesCapacity ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCanGetCapacity ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCapacityReceived ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerWaitToStartNewWeek ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerReadyToStartNewWeek ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerReadyToStartNewWeekProjectNotStarted ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerProjectFinished ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler () {
        this.fetchData()
      },
    },
  },
  methods: {
    ...mapActions('ui', {
      showSuccessNotification: 'showSuccessNotification',
      showErrorNotification: 'showErrorNotification',
    }),
    fetchData: _throttle(async function fetchData () {
      this.game = await Repositories.animation.multiproject.get(this.gameId)
      // Show message only during the first load (not on data refresh)
      this.loading = false
    }, 200, { leading: true, trailing: true }),
    async setCriticalResource (criticalResource) {
      try {
        this.loadingCritical = true
        await Repositories.animation.multiproject.patch({
          id: this.gameId,
          criticalResource,
        })
        this.game = await Repositories.animation.multiproject.get(this.gameId)
        this.showSuccessNotification(this.$t('animation.multiproject.criticalResource.success'))
      }
      catch (error) {
        const notification = Repositories.parseErrorMessage(error)
        this.showErrorNotification(notification)
      }
      finally {
        this.loadingCritical = false
      }
    },
    async sendPlayerAction (playerId, actionName) {
      try {
        await Repositories.animation.multiproject.sendAction({
          gameId: this.gameId,
          playerId,
          actionName,
        })
        this.fetchData()
      }
      catch (error) {
        this.showErrorNotification(Repositories.parseErrorMessage(error))
        console.error(error)
      }
    },
  },
  provide () {
    return {
      sendPlayerAction: this.sendPlayerAction,
    }
  },
}
</script>
